import type {Page} from '@PosterWhiteboard/page/page.class';
import {Group, Point} from '@postermywall/fabricjs-2';

const ALIGNMENT_PADDING = 0;

export enum GroupVeticalAlignment {
  TOP = 'top',
  BOTTOM = 'bottom',
  CENTER = 'center',
}

export enum GroupHorizontalAlignment {
  LEFT = 'left',
  RIGHT = 'right',
  CENTER = 'center',
}

export enum AlignType {
  CENTER = 'center',
  VERTICAL = 'vertical',
  HORIZONTAL = 'horizontal',
  LEFT = 'left',
  RIGHT = 'right',
  TOP = 'top',
  BOTTOM = 'bottom',
}

export class Alignment {
  public page: Page;

  public constructor(page: Page) {
    this.page = page;
  }

  public horizontalAlignment(horizontalAlignmentType: GroupHorizontalAlignment): void {
    const fabricObjects = this.page.activeSelection.getActiveObjects();
    if (!fabricObjects || fabricObjects.length === 0) {
      return;
    }

    const group = this.page.activeSelection.getActiveObject();
    if (!(group instanceof Group)) {
      return;
    }

    if (group.lockMovementX) {
      return;
    }

    group.horizontalAlignment(horizontalAlignmentType);
    this.page.fabricCanvas.requestRenderAll();
  }

  public verticalAlignment(verticalAlignmentType: GroupVeticalAlignment): void {
    const fabricObjects = this.page.activeSelection.getActiveObjects();
    if (!fabricObjects || fabricObjects.length === 0) {
      return;
    }

    const group = this.page.activeSelection.getActiveObject();
    if (!(group instanceof Group)) {
      return;
    }

    if (group.lockMovementX) {
      return;
    }

    group.verticalAlignment(verticalAlignmentType);
    this.page.fabricCanvas.requestRenderAll();
  }

  public alignItems(type: AlignType, undoable = true, fireModifiedEvent = true): void {
    const activeFabricObject = this.page.activeSelection.getActiveObject();
    if (!activeFabricObject) {
      return;
    }

    if (activeFabricObject.lockMovementX) {
      return;
    }

    const item = this.page.items.getItemForFabricObject(activeFabricObject);
    if (item && (item.isText() || item.isSlideshow())) {
      item.restoreDefaultState();
    }

    const items = this.page.getSelectedItems();
    const isGroup = items.length > 1;
    const canvasCenter = this.page.fabricCanvas.getCenterPoint();
    const viewCenter = activeFabricObject.getCenterPoint();
    const point = new Point({
      x: 0,
      y: 0,
    });

    switch (type) {
      case AlignType.VERTICAL:
        point.x = viewCenter.x;
        point.y = canvasCenter.y / this.page.poster.scaling.scale;
        break;
      case AlignType.HORIZONTAL:
        point.x = canvasCenter.x / this.page.poster.scaling.scale;
        point.y = viewCenter.y;
        break;
      case AlignType.CENTER:
        point.x = canvasCenter.x / this.page.poster.scaling.scale;
        point.y = canvasCenter.y / this.page.poster.scaling.scale;
        break;
      case AlignType.LEFT: {
        const c = activeFabricObject.getCornerPoints(viewCenter);
        const minX = Math.min(c.tl.x, c.tr.x, c.bl.x, c.br.x);
        point.x = viewCenter.x - minX + ALIGNMENT_PADDING;
        point.y = viewCenter.y;
        break;
      }
      case AlignType.RIGHT: {
        const c = activeFabricObject.getCornerPoints(viewCenter);
        const maxX = Math.max(c.tl.x, c.tr.x, c.bl.x, c.br.x);
        const canvasWidth = this.page.poster.width;
        point.x = viewCenter.x + canvasWidth - maxX - ALIGNMENT_PADDING;
        point.y = viewCenter.y;
        break;
      }
      case AlignType.TOP: {
        const c = activeFabricObject.getCornerPoints(viewCenter);
        const minY = Math.min(c.tl.y, c.tr.y, c.bl.y, c.br.y);
        point.x = viewCenter.x;
        point.y = viewCenter.y - minY + ALIGNMENT_PADDING;
        break;
      }
      case AlignType.BOTTOM: {
        const c = activeFabricObject.getCornerPoints(viewCenter);
        const maxY = Math.max(c.tl.y, c.tr.y, c.bl.y, c.br.y);
        const canvasHeight = this.page.poster.height;
        point.x = viewCenter.x;
        point.y = viewCenter.y + canvasHeight - maxY - ALIGNMENT_PADDING;
        break;
      }
      default:
        return;
    }

    const corner = activeFabricObject.getCornerPoints(point);
    // In case of group we are using fabric.object set() function to move the group on canvas as for now we do not
    // have a group mediator
    if (isGroup) {
      activeFabricObject.set({
        left: Math.round(corner.tl.x),
        top: Math.round(corner.tl.y),
      });
      activeFabricObject.setCoords();

      if (undoable) {
        this.page.poster.history.addPosterHistory();
      }
      if (fireModifiedEvent) {
        this.page.fire('modified');
      }
      this.page.poster.redux.updateReduxData();
      this.page.fabricCanvas.requestRenderAll();
    } else {
      void items[0].updateFromObject(
        {
          left: Math.round(corner.tl.x),
          top: Math.round(corner.tl.y),
        },
        {undoable, fireModifiedEvent}
      );
    }
  }
}
